// Role-based feature configuration
import {
  getDomainHostname,
  isMobileLikeWebDomain,
  MOBILE_LIKE_WEB_HOSTS,
} from "../utils/domainResolver";

export const USER_ROLES = {
  ADMIN: 'ADMIN',
  EMPLOYEE: 'EMPLOYEE',
  STOCK_MANAGER: 'STOCK_MANAGER',
};

export const FEATURES = {
  DASHBOARD: 'dashboard',
  POS: 'pos',
  ORDERS: 'orders',
  SALES: 'sales',
  SALES_METRICS: 'sales_metrics',
  SALES_PROFIT: 'sales_profit',
  SALES_SUMMARY: "sales_summary",
  PREVIOUS_BALANCE: 'previous_balance',
  FINANCE: 'finance',
  PRODUCTS: 'products',
  PRODUCTS_WRITE: 'products_write', // Add/Edit products
  CUSTOMERS: 'customers',
  VENDORS: 'vendors',
  IMEI: 'imei',
  RETURNS: 'returns',
  SETTINGS: 'settings',
  REPORTS: 'reports',
  INVENTORY: 'inventory',
  EMPLOYEE_MANAGEMENT: "employee_management",
  BRANDS: 'brands',
  CATEGORIES: 'categories',
  BATCHES: 'batches',
  PURCHASES: 'purchases',
  LOW_STOCK: 'low_stock',
  TRANSFER_HISTORY: 'transfer_history',
  PRICING_CATALOG: 'pricing_catalog',
  PROFIT_MANAGEMENT: 'profit_management',
  GODDOWN: "godown",
  TRANSFER_TO_POS: "transfer_to_pos"
};

export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  EXPORT: 'export',
  PRINT: 'print'
};

const STAFF_SALES_PERMISSIONS = [
  PERMISSIONS.READ,
  PERMISSIONS.WRITE,
  PERMISSIONS.PRINT,
];

const FULL_MODULE_ACCESS = [
  PERMISSIONS.READ,
  PERMISSIONS.WRITE,
  PERMISSIONS.PRINT,
  PERMISSIONS.EXPORT,
];

const EMPLOYEE_FEATURES = {
  [FEATURES.POS]: [PERMISSIONS.READ, PERMISSIONS.WRITE],
  [FEATURES.ORDERS]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
  [FEATURES.SALES]: STAFF_SALES_PERMISSIONS,
  [FEATURES.SETTINGS]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
  [FEATURES.PRICING_CATALOG]: FULL_MODULE_ACCESS,
};

// Stock manager: hide dashboard & finance only; show other modules; sales same as employee
const STOCK_MANAGER_FEATURES = {
  [FEATURES.POS]: [PERMISSIONS.READ, PERMISSIONS.WRITE],
  [FEATURES.ORDERS]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
  [FEATURES.SALES]: STAFF_SALES_PERMISSIONS,
  [FEATURES.PRODUCTS]: [PERMISSIONS.READ, PERMISSIONS.WRITE],
  [FEATURES.CUSTOMERS]: FULL_MODULE_ACCESS,
  [FEATURES.VENDORS]: FULL_MODULE_ACCESS,
  [FEATURES.SETTINGS]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
  [FEATURES.PRICING_CATALOG]: FULL_MODULE_ACCESS,
  [FEATURES.BRANDS]: FULL_MODULE_ACCESS,
  [FEATURES.CATEGORIES]: FULL_MODULE_ACCESS,
  [FEATURES.BATCHES]: FULL_MODULE_ACCESS,
  [FEATURES.PURCHASES]: FULL_MODULE_ACCESS,
  [FEATURES.LOW_STOCK]: FULL_MODULE_ACCESS,
  [FEATURES.TRANSFER_HISTORY]: FULL_MODULE_ACCESS,
  [FEATURES.IMEI]: FULL_MODULE_ACCESS,
  [FEATURES.RETURNS]: FULL_MODULE_ACCESS,
  [FEATURES.INVENTORY]: FULL_MODULE_ACCESS,
  [FEATURES.TRANSFER_TO_POS]: FULL_MODULE_ACCESS,
  [FEATURES.GODDOWN]: FULL_MODULE_ACCESS,
};

const domain = getDomainHostname();
// let baseURL = "";

// if (domain === process.env.REACT_APP_WEB_PARTS_URL) {
//   baseURL = process.env.REACT_APP_PARTS_URL;
// } else if (domain === process.env.REACT_APP_WEB_ACCESSORIES_URL) {
//   baseURL = process.env.REACT_APP_ACCESSORIES_URL;
// } else if (domain === process.env.REACT_APP_WEB_MOBILE_URL) {
//   baseURL = process.env.REACT_APP_API_URL;
// } else if (domain === process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL) {
//   baseURL = process.env.REACT_APP_ACCESSORIES_GODDOWN_URL;
// } else if (domain === process.env.REACT_APP_LOCAL_URL) {
//   baseURL = process.env.REACT_APP_ACCESSORIES_URL;
// }

const MOBILE_LIKE_BLOCKED_FEATURES = [
  FEATURES.TRANSFER_HISTORY,
  FEATURES.PRODUCTS_WRITE,
  FEATURES.GODDOWN,
  FEATURES.TRANSFER_TO_POS,
];

const MOBILE_LIKE_BASE_ROUTING = {
  [USER_ROLES.ADMIN]: '/dashboard',
  [USER_ROLES.EMPLOYEE]: '/pos',
  [USER_ROLES.STOCK_MANAGER]: '/pos',
};

const MOBILE_LIKE_PRODUCT_TABS = [
  { label: "Products", value: "products" },
  { label: "Brands", value: "brands" },
  { label: "Categories", value: "categories" },
  { label: "Batches", value: "batches" },
  { label: "Purchases", value: "purchases" },
  { label: "Low Stock", value: "low-stock" },
  { label: "Pricing Catalog", value: "pricing-catalog" },
];

export const MOBILE_LIKE_WEB_DOMAINS = MOBILE_LIKE_WEB_HOSTS;

/** Berlin and Handywelt use the same feature/routing rules as live.mobile. */
export { isMobileLikeWebDomain };

const BLOCKED_FEATURES_BY_DOMAIN = {
  [process.env.REACT_APP_WEB_ACCESSORIES_URL]: [
    FEATURES.IMEI, FEATURES.RETURNS, FEATURES.GODDOWN, FEATURES.TRANSFER_TO_POS,
    FEATURES.BRANDS, FEATURES.CATEGORIES, FEATURES.BATCHES, FEATURES.PURCHASES,
    FEATURES.PRODUCTS_WRITE, FEATURES.PRICING_CATALOG,
  ],
  [process.env.REACT_APP_WEB_PARTS_URL]: [
    FEATURES.GODDOWN, FEATURES.TRANSFER_TO_POS,
    FEATURES.PRODUCTS_WRITE, FEATURES.TRANSFER_HISTORY
  ],
  [process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL]: [
    FEATURES.IMEI, FEATURES.RETURNS, FEATURES.GODDOWN,
    FEATURES.DASHBOARD, FEATURES.POS, FEATURES.ORDERS, FEATURES.SALES,
    FEATURES.FINANCE, FEATURES.CUSTOMERS, FEATURES.TRANSFER_HISTORY,
    FEATURES.PRICING_CATALOG,
  ],
  [process.env.REACT_APP_WEB_MOBILE_URL]: MOBILE_LIKE_BLOCKED_FEATURES,
  [process.env.REACT_APP_WEB_BERLIN_URL]: MOBILE_LIKE_BLOCKED_FEATURES,
  [process.env.REACT_APP_WEB_LOYCOM_URL]: [
    FEATURES.TRANSFER_HISTORY,
    FEATURES.PRODUCTS_WRITE,
    FEATURES.GODDOWN,
    FEATURES.TRANSFER_TO_POS,
    FEATURES.PRICING_CATALOG,
    FEATURES.PROFIT_MANAGEMENT,
  ],
  [process.env.REACT_APP_WEB_HANDYWELT_URL]: MOBILE_LIKE_BLOCKED_FEATURES,
};

const BLOCKED_FEATURES = BLOCKED_FEATURES_BY_DOMAIN[domain] ?? [];
// Base routing configuration by domain and role
export const BASE_ROUTING_BY_DOMAIN = {
  [process.env.REACT_APP_WEB_ACCESSORIES_URL]: {
    [USER_ROLES.ADMIN]: '/dashboard',
    [USER_ROLES.EMPLOYEE]: '/pos',
    [USER_ROLES.STOCK_MANAGER]: '/pos',
  },
  [process.env.REACT_APP_WEB_PARTS_URL]: {
    [USER_ROLES.ADMIN]: '/dashboard',
    [USER_ROLES.EMPLOYEE]: '/pos',
    [USER_ROLES.STOCK_MANAGER]: '/pos',
  },
  [process.env.REACT_APP_WEB_MOBILE_URL]: MOBILE_LIKE_BASE_ROUTING,
  [process.env.REACT_APP_WEB_BERLIN_URL]: MOBILE_LIKE_BASE_ROUTING,
  [process.env.REACT_APP_WEB_HANDYWELT_URL]: MOBILE_LIKE_BASE_ROUTING,
  [process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL]: {
    [USER_ROLES.ADMIN]: '/products',
    [USER_ROLES.EMPLOYEE]: '/products',
    [USER_ROLES.STOCK_MANAGER]: '/products',
  },
};

export const getBaseRouting = (userRole) => {
  const domainRouting = BASE_ROUTING_BY_DOMAIN[domain];
  const route = domainRouting?.[userRole];
  if (route) return route;
  if (
    userRole === USER_ROLES.EMPLOYEE ||
    userRole === USER_ROLES.STOCK_MANAGER
  ) {
    return '/pos';
  }
  return '/dashboard';
};

export const isLimitedStaffRole = (userRole) =>
  userRole === USER_ROLES.EMPLOYEE || userRole === USER_ROLES.STOCK_MANAGER;

/** Sales UI restrictions shared by employee and stock manager (not admin). */
export const hasStaffLevelSalesRestrictions = (userRole) =>
  isLimitedStaffRole(userRole);

export default BLOCKED_FEATURES;

// Role-based feature access configuration
export const ROLE_FEATURES = {
  [USER_ROLES.EMPLOYEE]: EMPLOYEE_FEATURES,
  [USER_ROLES.STOCK_MANAGER]: STOCK_MANAGER_FEATURES,
};

// Helper functions
export const getUserRole = (user) => {
  const role = user?.admin_role || user?.role;
  if (!role || !String(role).trim()) return undefined;
  return String(role).trim().toUpperCase().replace(/[\s-]+/g, "_");
};

const ROLE_LABELS = {
  [USER_ROLES.ADMIN]: "Admin",
  [USER_ROLES.EMPLOYEE]: "Employee",
  [USER_ROLES.STOCK_MANAGER]: "Stock Manager",
};

/** Human-readable role label without underscores (e.g. STOCK_MANAGER → Stock Manager). */
export const formatRoleLabel = (role) => {
  if (!role) return "";
  const normalized = String(role).trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (ROLE_LABELS[normalized]) {
    return ROLE_LABELS[normalized];
  }
  return normalized
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

export const getRoleBadgeClassName = (role) => {
  const normalized = String(role || "").trim().toUpperCase();
  if (normalized === USER_ROLES.ADMIN) return "emp-role-admin";
  if (normalized === USER_ROLES.EMPLOYEE) return "emp-role-employee";
  if (normalized === USER_ROLES.STOCK_MANAGER) return "emp-role-stock-manager";
  return "";
};

export const hasFeatureAccess = (userRole, feature) => {
  // Check if feature is blocked
  if (BLOCKED_FEATURES.includes(feature)) return false;

  if (userRole === USER_ROLES.ADMIN) return true;
  return ROLE_FEATURES[userRole]?.[feature] !== undefined;
};

export const hasPermission = (userRole, feature, permission) => {
  // Check if feature is blocked
  if (BLOCKED_FEATURES.includes(feature)) return false;

  if (userRole === USER_ROLES.ADMIN) return true;
  return ROLE_FEATURES[userRole]?.[feature]?.includes(permission) || false;
};

export const getUserFeatures = (userRole) => {
  const allFeatures = userRole === USER_ROLES.ADMIN
    ? Object.values(FEATURES)
    : Object.keys(ROLE_FEATURES[userRole] || {});

  // Filter out blocked features
  return allFeatures.filter(feature => !BLOCKED_FEATURES.includes(feature));
};

export const getFeaturePermissions = (userRole, feature) => {
  // Check if feature is blocked
  if (BLOCKED_FEATURES.includes(feature)) return [];

  if (userRole === USER_ROLES.ADMIN) return Object.values(PERMISSIONS);
  return ROLE_FEATURES[userRole]?.[feature] || [];
};

// Helper function to check if a specific feature is blocked for the current domain
export const isFeatureBlocked = (feature) => {
  return BLOCKED_FEATURES.includes(feature);
};

export const EMPLOYEE_PRODUCT_RESTRICTED_PATHS = [
  '/products',
  '/brands',
  '/category',
  '/batches',
  '/purchases',
  '/low-stock',
  '/transfer-history',
  '/tags',
];

export const isEmployeePricingCatalogOnly = (userRole) => {
  return (
    userRole === USER_ROLES.EMPLOYEE &&
    hasFeatureAccess(userRole, FEATURES.PRICING_CATALOG)
  );
};

// Header configuration by domain and path
export const HEADER_CONFIG = {
  products: {
    [process.env.REACT_APP_WEB_ACCESSORIES_URL]: [
      { label: "Products", value: "products" },
      // { label: "Brands", value: "brands" },
      // { label: "Categories", value: "categories" },
      // { label: "Batches", value: "batches" },
      // { label: "Purchases", value: "purchases" },
      { label: "Transfer History", value: "transfer-history" },
      { label: "Low Stock", value: "low-stock" },
    ],
    [process.env.REACT_APP_WEB_PARTS_URL]: [
      { label: "Products", value: "products" },
      { label: "Brands", value: "brands" },
      { label: "Categories", value: "categories" },
      { label: "Batches", value: "batches" },
      { label: "Purchases", value: "purchases" },
      { label: "Low Stock", value: "low-stock" },
      { label: "Pricing Catalog", value: "pricing-catalog" },
    ],
    [process.env.REACT_APP_WEB_MOBILE_URL]: MOBILE_LIKE_PRODUCT_TABS,
    [process.env.REACT_APP_WEB_BERLIN_URL]: MOBILE_LIKE_PRODUCT_TABS,
    [process.env.REACT_APP_WEB_HANDYWELT_URL]: MOBILE_LIKE_PRODUCT_TABS,
    [process.env.REACT_APP_WEB_LOYCOM_URL]: [
      { label: "Products", value: "products" },
      { label: "Brands", value: "brands" },
      { label: "Categories", value: "categories" },
      { label: "Batches", value: "batches" },
      { label: "Purchases", value: "purchases" },
      { label: "Low Stock", value: "low-stock" },
    ],
    [process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL]: [
      { label: "Products", value: "products" },
      { label: "Brands", value: "brands" },
      { label: "Categories", value: "categories" },
      { label: "Tags", value: "tags" },
      { label: "Batches", value: "batches" },
      { label: "Purchases", value: "purchases" },
      { label: "Low Stock", value: "low-stock" },
    ],
  },
  customers: [
    { label: "Customers", value: "customers" },
  ],
  finance: [
    { label: "Journal", value: "journal" },
    { label: "Reports", value: "reports" },
    { label: "Profit Management", value: "overview" },
  ],
  returns: [
    { label: "Register", value: "register" },
    { label: "Repair Reserve", value: "repair-reserve" },
    { label: "Supplier Returns", value: "vendor-returns" },
  ],
};

export const getHeaderLabels = (pathname, userRole) => {
  const filterBlockedProductTabs = (labels) => {
    if (!labels) return [];
    return labels.filter((item) => {
      if (item.value === "pricing-catalog" && isFeatureBlocked(FEATURES.PRICING_CATALOG)) {
        return false;
      }
      return true;
    });
  };

  const filterEmployeeProductTabs = (labels) => {
    if (!isEmployeePricingCatalogOnly(userRole)) return labels;
    return labels.filter((item) => item.value === "pricing-catalog");
  };

  if (pathname === "/products" || pathname.startsWith("/product/") ||
    pathname === "/brands" || pathname === "/category" || 
    pathname === "/transfer-history" || pathname.startsWith("/transfer-history/") ||
    pathname === "/batches" || pathname === "/purchases" ||
    pathname === "/low-stock" || pathname === "/tags" || pathname.startsWith("/tags/") ||
    pathname === "/pricing-catalog") {
    const labels = HEADER_CONFIG.products[domain] || HEADER_CONFIG.products[process.env.REACT_APP_WEB_ACCESSORIES_URL];
    return filterEmployeeProductTabs(filterBlockedProductTabs(labels));
  }
  if (pathname === "/customers" || pathname.startsWith("/customer/")) {
    return HEADER_CONFIG.customers;
  }
  if (pathname === "/finance" || pathname.startsWith("/finance/")) {
    if (isFeatureBlocked(FEATURES.PROFIT_MANAGEMENT)) {
      return HEADER_CONFIG.finance.filter((item) => item.value !== "overview");
    }
    return HEADER_CONFIG.finance;
  }
  if (pathname.startsWith("/returns")) {
    return HEADER_CONFIG.returns;
  }
  return [];
};